<template>
  <v-dialog
    :value="visible || dialog"
    @input="$emit('close')"
    max-width="1200px"
    scrollable
    persistent
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon left color="primary">mdi-code-json</v-icon>
        <span>{{ isEditing ? 'Edit' : 'Create' }} Extract Template</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6" style="max-height: 70vh;">
        <v-form ref="templateForm" v-model="formValid">
          <div class="mb-6">
            <div class="text-h6 mb-4">Template Information</div>

            <v-text-field
              v-model="localTemplate.name"
              outlined
              label="Template Name"
              placeholder="e.g., Invoice Data, Medical Report, Contract Details"
              :rules="[rules.required]"
              class="mb-3"
            />

            <v-textarea
              v-model="localTemplate.description"
              outlined
              label="Template Description"
              placeholder="Describe what data this template extracts from documents..."
              :rules="[rules.required, rules.minLength]"
              rows="3"
            />
          </div>

          <v-divider class="mb-6"></v-divider>

          <div class="mb-6">
            <div class="d-flex align-center mb-4">
              <div class="text-h6">Extract Fields</div>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                outlined
                @click="addField"
              >
                <v-icon left small>mdi-plus</v-icon>
                Add Field
              </v-btn>
            </div>

            <div v-if="hasFields" class="fields-container">
              <div v-for="(field, fieldName, index) in localTemplate.schema.properties"
                   :key="`field-${index}`"
                   class="mb-4">
                <extract-field-editor
                  :field-name="fieldName"
                  :field="field"
                  :index="index"
                  :is-required="isRequiredField(fieldName)"
                  :parent-path="''"
                  @update-field="updateField"
                  @update-field-name="updateFieldName"
                  @remove-field="removeField"
                  @toggle-required="toggleRequired"
                />
              </div>
            </div>

            <div v-else class="text-center py-8">
              <v-icon large color="grey lighten-1" class="mb-3">mdi-code-json</v-icon>
              <div class="text-h6 mb-2 grey--text">No Fields Defined</div>
              <div class="text-body-2 grey--text mb-4">
                Add fields to define what data should be extracted from documents
              </div>
              <v-btn
                color="primary"
                outlined
                @click="addField"
              >
                <v-icon left>mdi-plus</v-icon>
                Add First Field
              </v-btn>
            </div>
          </div>

          <v-divider class="mb-6"></v-divider>

          <div class="mb-4">
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <v-icon left small>mdi-code-json</v-icon>
                    <span class="text-h6">JSON Schema Preview</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card outlined class="pa-3 mt-3" style="background-color: #f8f9fa;">
                    <pre class="text-caption schema-preview">{{ formattedSchema }}</pre>
                  </v-card>

                  <v-btn
                    text
                    small
                    color="primary"
                    class="mt-3"
                    @click="toggleJsonEditor"
                  >
                    <v-icon left small>mdi-pencil</v-icon>
                    {{ showJsonEditor ? 'Hide' : 'Edit' }} Raw JSON
                  </v-btn>

                  <div v-if="showJsonEditor" class="mt-4">
                    <v-textarea
                      v-model="jsonEditorValue"
                      outlined
                      label="Raw JSON Schema"
                      rows="12"
                      :error-messages="jsonEditorError"
                      class="json-editor"
                    />
                    <div class="d-flex gap-2">
                      <v-btn
                        small
                        color="primary"
                        @click="applyJsonChanges"
                        :disabled="!!jsonEditorError"
                      >
                        Apply Changes
                      </v-btn>
                      <v-btn
                        small
                        text
                        @click="cancelJsonEditor"
                      >
                        Cancel
                      </v-btn>
                    </div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-6">
        <v-btn
          text
          @click="$emit('close')"
        >
          Cancel
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="saveTemplate"
          :disabled="!formValid || !hasValidFields"
          :loading="saving"
        >
          {{ isEditing ? 'Save Changes' : 'Create Template' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ExtractFieldEditor from './ExtractFieldEditor.vue';

export default {
  name: 'ExtractTemplateModal',
  components: {
    ExtractFieldEditor,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    dialog: {
      type: Boolean,
      default: false
    },
    template: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'connector',
      validator: (value) => ['connector', 'database'].includes(value)
    }
  },
  data() {
    return {
      formValid: false,
      saving: false,
      showJsonEditor: false,
      jsonEditorValue: '',
      jsonEditorError: null,
      localTemplate: {
        name: '',
        description: '',
        schema: {
          type: 'object',
          properties: {},
          required: []
        }
      },
      rules: {
        required: v => !!v || 'This field is required',
        minLength: v => (v && v.length >= 10) || 'Minimum 10 characters required'
      }
    };
  },
  computed: {
    isEditing() {
      return !!this.template;
    },
    hasFields() {
      return Object.keys(this.localTemplate.schema.properties || {}).length > 0;
    },
    hasValidFields() {
      if (!this.hasFields) return false;
      return this.localTemplate.name && this.localTemplate.description && this.localTemplate.description.length >= 10;
    },
    formattedSchema() {
      return JSON.stringify(this.localTemplate.schema, null, 2);
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeTemplate();
      }
    },
    dialog(newVal) {
      if (newVal) {
        this.initializeTemplate();
      }
    },
    template: {
      handler() {
        if (this.visible || this.dialog) {
          this.initializeTemplate();
        }
      },
      deep: true
    },
    jsonEditorValue(newVal) {
      this.validateJson(newVal);
    }
  },
  methods: {
    initializeTemplate() {
      if (this.template) {
        this.localTemplate = JSON.parse(JSON.stringify(this.template));
      } else {
        this.localTemplate = {
          name: '',
          description: '',
          schema: {
            type: 'object',
            properties: {},
            required: []
          }
        };
      }
      this.showJsonEditor = false;
      this.jsonEditorError = null;
    },

    addField() {
      const fieldName = `field${Object.keys(this.localTemplate.schema.properties).length + 1}`;
      const newField = {
        type: 'string',
        description: ''
      };

      this.localTemplate.schema.properties = {
        ...this.localTemplate.schema.properties,
        [fieldName]: newField
      };
    },

    updateField(fieldName, property, value, parentPath = '', isFullReplace = false) {
      if (parentPath) {
        this.updateNestedField(this.localTemplate, parentPath, fieldName, property, value, isFullReplace);
      } else {
        if (isFullReplace) {
          this.localTemplate.schema.properties[fieldName] = value;
        } else if (property) {
          this.localTemplate.schema.properties[fieldName] = {
            ...this.localTemplate.schema.properties[fieldName],
            [property]: value
          };
        }
      }
    },

    updateFieldName(oldName, newName, parentPath = '') {
      if (oldName === newName || !newName.trim()) return;

      if (parentPath) {
        this.updateNestedFieldName(this.localTemplate, parentPath, oldName, newName);
      } else {
        const field = this.localTemplate.schema.properties[oldName];
        const newProperties = {};

        for (const [key, value] of Object.entries(this.localTemplate.schema.properties)) {
          if (key === oldName) {
            newProperties[newName] = field;
          } else {
            newProperties[key] = value;
          }
        }

        const newRequired = (this.localTemplate.schema.required || []).map(req =>
          req === oldName ? newName : req
        );

        this.localTemplate.schema = {
          ...this.localTemplate.schema,
          properties: newProperties,
          required: newRequired
        };
      }
    },

    removeField(fieldName, parentPath = '') {
      if (parentPath) {
        this.removeNestedField(this.localTemplate, parentPath, fieldName);
      } else {
        const { [fieldName]: removed, ...remainingProperties } = this.localTemplate.schema.properties;
        console.log(removed)

        const newRequired = (this.localTemplate.schema.required || []).filter(req => req !== fieldName);

        this.localTemplate.schema = {
          ...this.localTemplate.schema,
          properties: remainingProperties,
          required: newRequired
        };
      }
    },

    toggleRequired(fieldName, isRequired, parentPath = '') {
      if (parentPath) {
        this.toggleNestedRequiredField(this.localTemplate, parentPath, fieldName, isRequired);
      } else {
        let newRequired = [...(this.localTemplate.schema.required || [])];

        if (isRequired && !newRequired.includes(fieldName)) {
          newRequired.push(fieldName);
        } else if (!isRequired) {
          newRequired = newRequired.filter(req => req !== fieldName);
        }

        this.localTemplate.schema = {
          ...this.localTemplate.schema,
          required: newRequired
        };
      }
    },

    isRequiredField(fieldName) {
      return this.localTemplate.schema.required?.includes(fieldName) || false;
    },

    toggleJsonEditor() {
      if (!this.showJsonEditor) {
        this.jsonEditorValue = this.formattedSchema;
        this.jsonEditorError = null;
      }
      this.showJsonEditor = !this.showJsonEditor;
    },

    validateJson(value) {
      try {
        const parsedSchema = JSON.parse(value);

        const validationErrors = this.validateSchemaStructure(parsedSchema);

        if (validationErrors.length > 0) {
          this.jsonEditorError = `Schema validation failed: ${validationErrors.join(', ')}`;
          return false;
        }

        this.jsonEditorError = null;
        return true;
      } catch (error) {
        let errorMessage = 'Invalid JSON syntax: ';

        if (error.message.includes('Unexpected token')) {
          if (error.message.includes('Unexpected token }')) {
            errorMessage += 'Extra closing brace "}" - check for missing commas or quotes';
          } else if (error.message.includes('Unexpected token ]')) {
            errorMessage += 'Extra closing bracket "]" - check array structure';
          } else if (error.message.includes('Unexpected token ,')) {
            errorMessage += 'Extra comma "," - remove trailing commas';
          } else if (error.message.includes('Unexpected string')) {
            errorMessage += 'Missing comma between properties or invalid quotes';
          } else {
            errorMessage += 'Unexpected character - check quotes, commas, and brackets';
          }
        } else if (error.message.includes('Unexpected end')) {
          errorMessage += 'Incomplete JSON - missing closing brace "}" or bracket "]"';
        } else if (error.message.includes('Expected property name')) {
          errorMessage += 'Property names must be in double quotes "propertyName"';
        } else {
          errorMessage += error.message;
        }

        this.jsonEditorError = errorMessage;
        return false;
      }
    },

    validateSchemaStructure(schema) {
      const errors = [];

      if (typeof schema !== 'object' || schema === null || Array.isArray(schema)) {
        errors.push('Schema must be an object');
        return errors;
      }

      if (!schema.type) {
        errors.push('Schema must have a "type" property');
      } else if (schema.type !== 'object') {
        errors.push('Root schema type must be "object"');
      }

      if (!schema.properties) {
        errors.push('Schema must have a "properties" object');
      } else if (typeof schema.properties !== 'object' || Array.isArray(schema.properties)) {
        errors.push('Schema "properties" must be an object');
      } else {
        const propertyErrors = this.validateProperties(schema.properties, 'properties');
        errors.push(...propertyErrors);
      }

      if (schema.required !== undefined) {
        if (!Array.isArray(schema.required)) {
          errors.push('Schema "required" must be an array');
        } else {
          const invalidRequired = schema.required.filter(req =>
            typeof req !== 'string' || !schema.properties || !schema.properties[req]
          );
          if (invalidRequired.length > 0) {
            errors.push(`Required fields don't exist in properties: ${invalidRequired.join(', ')}`);
          }
        }
      }

      return errors;
    },

    validateProperties(properties, path = '') {
      const errors = [];

      for (const [fieldName, field] of Object.entries(properties)) {
        const fieldPath = path ? `${path}.${fieldName}` : fieldName;

        if (typeof field !== 'object' || field === null || Array.isArray(field)) {
          errors.push(`Field "${fieldPath}" must be an object`);
          continue;
        }

        if (!field.type) {
          errors.push(`Field "${fieldPath}" must have a "type" property`);
        } else if (!['string', 'number', 'integer', 'boolean', 'array', 'object'].includes(field.type)) {
          errors.push(`Field "${fieldPath}" has invalid type "${field.type}"`);
        }

        if (field.type === 'object') {
          if (field.properties && typeof field.properties !== 'object') {
            errors.push(`Field "${fieldPath}.properties" must be an object`);
          } else if (field.properties) {
            const nestedErrors = this.validateProperties(field.properties, fieldPath);
            errors.push(...nestedErrors);
          }

          if (field.required !== undefined && !Array.isArray(field.required)) {
            errors.push(`Field "${fieldPath}.required" must be an array`);
          }
        }

        if (field.type === 'array') {
          if (field.items) {
            if (typeof field.items !== 'object' || !field.items.type) {
              errors.push(`Field "${fieldPath}.items" must have a valid "type" property`);
            }
          }
        }
      }

      return errors;
    },

    applyJsonChanges() {
      if (!this.validateJson(this.jsonEditorValue)) {
        return;
      }

      try {
        const newSchema = JSON.parse(this.jsonEditorValue);

        this.localTemplate.schema = newSchema;
        this.showJsonEditor = false;
        this.jsonEditorError = null;
      } catch (error) {
        this.jsonEditorError = `Unexpected error: ${error.message}`;
      }
    },

    cancelJsonEditor() {
      this.showJsonEditor = false;
      this.jsonEditorValue = '';
      this.jsonEditorError = null;
    },

    async saveTemplate() {
      if (!this.$refs.templateForm.validate()) {
        return;
      }

      this.saving = true;
      try {
        const templateData = {
          ...this.localTemplate,
          schema: JSON.parse(JSON.stringify(this.localTemplate.schema)) // Deep copy
        };

        this.$emit('save', templateData);
        this.$emit('close');
      } catch (error) {
        console.error('Error saving template:', error);
      } finally {
        this.saving = false;
      }
    },

    getNestedObject(obj, path) {
      return path.split('.').reduce((current, key) => {
        if (current && current.properties && current.properties[key]) {
          return current.properties[key];
        }
        return null;
      }, obj);
    },

    updateNestedField(template, parentPath, fieldName, property, value, isFullReplace = false) {
      const parentObj = this.getNestedObject(template.schema, parentPath);

      if (parentObj && parentObj.properties) {
        if (isFullReplace) {
          parentObj.properties[fieldName] = value;
        } else if (property) {
          if (!parentObj.properties[fieldName]) {
            parentObj.properties[fieldName] = {};
          }
          parentObj.properties[fieldName][property] = value;
        }
      }
    },

    updateNestedFieldName(template, parentPath, oldName, newName) {
      const parentObj = this.getNestedObject(template.schema, parentPath);

      if (parentObj && parentObj.properties && parentObj.properties[oldName]) {
        const field = parentObj.properties[oldName];
        const newProperties = {};

        for (const [key, value] of Object.entries(parentObj.properties)) {
          if (key === oldName) {
            newProperties[newName] = field;
          } else {
            newProperties[key] = value;
          }
        }

        parentObj.properties = newProperties;

        if (parentObj.required && Array.isArray(parentObj.required)) {
          const requiredIndex = parentObj.required.indexOf(oldName);
          if (requiredIndex !== -1) {
            parentObj.required[requiredIndex] = newName;
          }
        }
      }
    },

    removeNestedField(template, parentPath, fieldName) {
      const parentObj = this.getNestedObject(template.schema, parentPath);

      if (parentObj && parentObj.properties) {
        delete parentObj.properties[fieldName];

        if (parentObj.required && Array.isArray(parentObj.required)) {
          parentObj.required = parentObj.required.filter(req => req !== fieldName);
        }
      }
    },

    toggleNestedRequiredField(template, parentPath, fieldName, isRequired) {
      const parentObj = this.getNestedObject(template.schema, parentPath);

      if (parentObj) {
        if (!parentObj.required) parentObj.required = [];

        if (isRequired && !parentObj.required.includes(fieldName)) {
          parentObj.required.push(fieldName);
        } else if (!isRequired) {
          parentObj.required = parentObj.required.filter(req => req !== fieldName);
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
.fields-container {
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background-color: #fafafa;
}

.schema-preview {
  margin: 0;
  white-space: pre-wrap;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-height: 300px;
  overflow-y: auto;
}

.json-editor {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.v-dialog .v-card {
  overflow: visible;
}

.gap-2 > * + * {
  margin-left: 8px;
}
</style>